// GlobalSearch — header search box that queries /api/search across employees,
// customers, products, finance reports, mystery evaluations and campaigns.
import React, { useEffect, useRef, useState } from "react";
import { api } from "./lib/api";
import { zVar } from "./lib/zIndex";
import { InfoTip } from "./components/InfoTip";

const TYPE_META = {
  employees: { icon: "👤", title: "موظفون", color: "#8b6f47" },
  customers: { icon: "👥", title: "عملاء", color: "#2e8b57" },
  products: { icon: "🍽", title: "منتجات", color: "#c9a44a" },
  finance: { icon: "💰", title: "تقارير مالية", color: "#3b82f6" },
  mystery: { icon: "🕵️", title: "تقييمات العميل السرّي", color: "#b9533e" },
  campaigns: { icon: "📣", title: "حملات", color: "#7c3aed" },
};

export default function GlobalSearch({ onJump }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Debounced search — fire BOTH endpoints in parallel:
  //   /api/search/answer  → AI-style direct answer for known intents
  //   /api/search         → classic result list as fallback / context
  useEffect(() => {
    if (q.trim().length < 2) { setData(null); setAnswer(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const [aRes, sRes] = await Promise.allSettled([
          api.get(`/search/answer?q=${encodeURIComponent(q.trim())}`),
          api.get(`/search?q=${encodeURIComponent(q.trim())}`),
        ]);
        setAnswer(aRes.status === "fulfilled" ? aRes.value.data : null);
        setData(sRes.status === "fulfilled" ? sRes.value.data : null);
        setOpen(true);
      } catch (_e) { /* silent */ }
      setBusy(false);
    }, 250);
  }, [q]);

  const total = data?.total || 0;
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 640;
  const hasAnswer = answer && answer.intent !== "unknown";
  // v119.197 — iOS-style: collapsed circular icon → expands on tap
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (expanded) inputRef.current?.focus(); }, [expanded]);
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) && !q.trim()) setExpanded(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [q]);

  if (!expanded) {
    return (
      <button type="button"
              data-testid="global-search-icon"
              onClick={() => setExpanded(true)}
 
              style={{
                width: 34, height: 34, borderRadius: "50%", border: "1px solid var(--border)",
                background: "var(--bg)", cursor: "pointer", fontSize: 15, lineHeight: 1,
                display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }} aria-label="بحث">
        🔍
      </button>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", flex: 1, maxWidth: 420 }} data-testid="global-search">
      <input
        ref={inputRef}
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => (data || answer) && setOpen(true)}
        placeholder={isMobile ? "🔍 ابحث..." : "🔍 اسأل أو ابحث: «نقطة التعادل؟»، «مبيعات لاتيه»…"}
        data-testid="global-search-input"
        style={{
          width: "100%", padding: "7px 14px", borderRadius: 22, border: "1px solid var(--border)",
          background: "var(--bg)", color: "var(--brown-deep)", fontSize: 13, outline: "none",
        }}
      />
      {open && q.trim().length >= 2 && (
        <div data-testid="global-search-results" style={{
          position: "absolute", insetInlineStart: 0, insetInlineEnd: 0, top: "calc(100% + 4px)",
          background: "#fff", color: "var(--brown-deep)", borderRadius: 10, boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          maxHeight: 480, overflowY: "auto", zIndex: zVar('extra_9999'), padding: 6,
        }}>
          {busy && !hasAnswer && <div style={{ padding: 14, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>⏳ يبحث…</div>}

          {/* AI-style answer card for recognized intents */}
          {hasAnswer && (
            <div
              data-testid="gs-answer"
              onClick={() => { if (answer.deep_link) onJump?.(answer.deep_link, answer); setOpen(false); setQ(""); }}
              style={{
                cursor: answer.deep_link ? "pointer" : "default",
                background: "linear-gradient(135deg, rgba(140,95,50,0.08), rgba(212,175,55,0.06))",
                border: "1px solid rgba(212,175,55,0.4)", borderRadius: 8, padding: 12, marginBottom: 6,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: "#8b6f47", fontWeight: 800, marginBottom: 4 }}>
                ✨ إجابة مباشرة
              </div>
              <div style={{ fontSize: 13.5, color: "var(--brown-deep)", lineHeight: 1.8, fontWeight: 600 }}>
                {answer.answer}
              </div>
              {answer.deep_link && (
                <div style={{ fontSize: 10.5, color: "#8b6f47", marginTop: 6 }}>← اضغط للذهاب للصفحة المفصّلة</div>
              )}
            </div>
          )}

          {/* Suggestions for unknown queries */}
          {answer?.intent === "unknown" && answer.examples && (
            <div style={{ padding: 10, background: "#fafafa", borderRadius: 8, marginBottom: 6, fontSize: 11.5 }} data-testid="gs-suggestions">
              <div style={{ fontWeight: 700, color: "var(--muted)", marginBottom: 4 }}>جرّب الأسئلة الجاهزة:</div>
              {answer.examples.map((ex, i) => (
                <button key={i} type="button" onClick={() => setQ(ex)} data-testid={`gs-suggestion-${i}`}
                  style={{ display: "block", width: "100%", textAlign: "start", padding: "4px 6px", background: "transparent", border: "none", color: "#8b6f47", cursor: "pointer", fontSize: 11.5 }}>
                  💡 {ex}
                </button>
              ))}
            </div>
          )}

          {!busy && total === 0 && !hasAnswer && answer?.intent !== "unknown" && (
            <div style={{ padding: 18, textAlign: "center", color: "var(--muted)", fontSize: 12.5 }}>
              لا توجد نتائج لـ «{q}»
            </div>
          )}
          {!busy && total > 0 && Object.entries(data.results).map(([type, items]) => {
            const meta = TYPE_META[type] || { icon: "•", title: type, color: "#888" };
            return (
              <div key={type} style={{ marginBottom: 6 }}>
                <div style={{ padding: "6px 10px", fontSize: 10.5, fontWeight: 800, color: meta.color, textTransform: "uppercase", letterSpacing: 1 }}>
                  {meta.icon} {meta.title} ({items.length})
                </div>
                {items.map((it, i) => (
                  <button
                    key={`${type}-${i}`}
                    type="button"
                    onClick={() => { onJump?.(type, it); setOpen(false); setQ(""); }}
                    data-testid={`gs-result-${type}-${i}`}
                    style={{
                      display: "block", width: "100%", textAlign: "start", padding: "8px 12px",
                      background: "transparent", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12.5,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#fafafa"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ fontWeight: 700, color: "var(--brown-deep)" }}>{it.label || "—"}</div>
                    {it.sub && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{it.sub}</div>}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
